import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { UserPlus, Loader2 } from 'lucide-react';
import apiService from '../../../services/api';
import { USER_ROLES } from '../constants';

const AddUserDialog = ({ open, onOpenChange, shopId, shopName, onSuccess }) => {
  const [userData, setUserData] = useState({
    fullname: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    role: 'shopTechnician',
  });
  const [errors, setErrors] = useState({});
  const [adding, setAdding] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (!userData.fullname.trim()) newErrors.fullname = 'Name is required';
    if (!userData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!userData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone is required';
    if (!userData.password) {
      newErrors.password = 'Password is required';
    } else if (userData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (userData.password !== userData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAdd = async () => {
    if (!validate()) return;
    
    setAdding(true);
    try {
      const response = await apiService.createShopUser({
        shopMongoId: shopId,
        fullname: userData.fullname,
        email: userData.email.toLowerCase(),
        phoneNumber: userData.phoneNumber,
        role: userData.role,
        password: userData.password,
        confirmPassword: userData.confirmPassword,
      });
      
      if (response.success) {
        onSuccess();
        handleClose();
      } else {
        setErrors({ general: response.message || 'Failed to add user' });
      }
    } catch (err) {
      setErrors({ general: err.message || 'Failed to add user' });
    } finally {
      setAdding(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setUserData({
      fullname: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      role: 'shopTechnician',
    });
    setErrors({});
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#1E293B] border-[#334155] text-[#E2E8F0]">
        <DialogHeader>
          <DialogTitle className="text-[#E2E8F0]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Add Shop User
          </DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Add a new user to {shopName}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {errors.general && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-md p-3">
              <p className="text-[#EF4444] text-sm">{errors.general}</p>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="userName" className="text-[#E2E8F0]">Full Name</Label>
            <Input
              id="userName"
              value={userData.fullname}
              onChange={(e) => setUserData(prev => ({ ...prev, fullname: e.target.value }))}
              placeholder="Enter full name"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.fullname ? 'border-[#EF4444]' : ''}`}
            />
            {errors.fullname && <p className="text-[#EF4444] text-xs">{errors.fullname}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="userEmail" className="text-[#E2E8F0]">Email</Label>
            <Input
              id="userEmail"
              type="email"
              value={userData.email}
              onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="user@example.com"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.email ? 'border-[#EF4444]' : ''}`}
            />
            {errors.email && <p className="text-[#EF4444] text-xs">{errors.email}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="userPhone" className="text-[#E2E8F0]">Phone</Label>
            <Input
              id="userPhone"
              value={userData.phoneNumber}
              onChange={(e) => setUserData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              placeholder="310-555-0102"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.phoneNumber ? 'border-[#EF4444]' : ''}`}
            />
            {errors.phoneNumber && <p className="text-[#EF4444] text-xs">{errors.phoneNumber}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="userPassword" className="text-[#E2E8F0]">Password</Label>
            <Input
              id="userPassword"
              type="password"
              value={userData.password}
              onChange={(e) => setUserData(prev => ({ ...prev, password: e.target.value }))}
              placeholder="Minimum 8 characters"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.password ? 'border-[#EF4444]' : ''}`}
            />
            {errors.password && <p className="text-[#EF4444] text-xs">{errors.password}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="userConfirmPassword" className="text-[#E2E8F0]">Confirm Password</Label>
            <Input
              id="userConfirmPassword"
              type="password"
              value={userData.confirmPassword}
              onChange={(e) => setUserData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Re-enter password"
              className={`bg-[#0F172A] border-[#334155] text-[#E2E8F0] ${errors.confirmPassword ? 'border-[#EF4444]' : ''}`}
            />
            {errors.confirmPassword && <p className="text-[#EF4444] text-xs">{errors.confirmPassword}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="userRole" className="text-[#E2E8F0]">Role</Label>
            <Select value={userData.role} onValueChange={(value) => setUserData(prev => ({ ...prev, role: value }))}>
              <SelectTrigger className="bg-[#0F172A] border-[#334155] text-[#E2E8F0]">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155]">
                {USER_ROLES.map(role => (
                  <SelectItem key={role.value} value={role.value} className="text-[#E2E8F0] focus:bg-[#334155] focus:text-[#E2E8F0]">
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-[#334155] text-[#94A3B8] hover:bg-[#334155]"
          >
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={adding}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Add User
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddUserDialog;
